// prevent-landscape.js
import React, { useEffect, useState } from "react";
import styled from "styled-components";

/* ---------- styles ---------- */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: #fffcf2;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem;
  pointer-events: auto;
  touch-action: none;
`;

const Card = styled.div`
  max-width: 30rem;
  line-height: 1.4;
  font-size: clamp(16px, 2.6vw, 20px);
`;

const Emoji = styled.div`
  font-size: 2.2rem;
  margin-bottom: 0.75rem;
`;

const Strong = styled.div`
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const Sub = styled.div`
  opacity: 0.8;
`;

const HiddenChildren = styled.div`
  display: ${(props) => (props.hidden ? "none" : "block")};
`;

/* ---------- helpers ---------- */
function isLandscape() {
  if (typeof window === "undefined") return false;
  // Prefer media query; fallback to dimensions
  if (window.matchMedia) {
    const mq = window.matchMedia("(orientation: landscape)");
    if (typeof mq.matches === "boolean") return mq.matches;
  }
  return (window.innerWidth || 0) > (window.innerHeight || 0);
}

function isBlocked(widthThreshold) {
  if (typeof window === "undefined") return false;
  const landscape = isLandscape();
  const narrow = (window.innerWidth || 0) < widthThreshold;
  return landscape && narrow;
}

/* ---------- component ---------- */
export default function PreventLandscape({
  widthThreshold = 900, // block landscape only when width is below this
  children,
  message = "For the best experience, use portrait orientation.",
  title = "Please rotate your device",
}) {
  const [blocked, setBlocked] = useState(isBlocked(widthThreshold));

  useEffect(() => {
    const check = () => setBlocked(isBlocked(widthThreshold));

    check();
    window.addEventListener("resize", check);
    window.addEventListener("orientationchange", check);

    // Some browsers expose visualViewport; changes there can also affect width
    const vv = window.visualViewport;
    vv?.addEventListener("resize", check);
    vv?.addEventListener("scroll", check);

    return () => {
      window.removeEventListener("resize", check);
      window.removeEventListener("orientationchange", check);
      vv?.removeEventListener("resize", check);
      vv?.removeEventListener("scroll", check);
    };
  }, [widthThreshold]);

  // Prevent background scroll when blocked
  useEffect(() => {
    if (!blocked) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [blocked]);

  return (
    <>
      <HiddenChildren hidden={blocked}>{children}</HiddenChildren>
      {blocked && (
        <Overlay role="dialog" aria-live="assertive" aria-modal="true">
          <Card>
            <Emoji>📱</Emoji>
            <Strong>{title}</Strong>
            <Sub>{message}</Sub>
          </Card>
        </Overlay>
      )}
    </>
  );
}
